import { Subject, asapScheduler, Subscription, Observable, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map, observeOn } from 'rxjs/operators';
// @ts-ignore
import curry from 'lodash/curry';
import { parseISO } from 'date-fns';
import { ISelectedRange } from 'phx-core/lib/date-picker/utils/types';
import { moment, IMoment, IMomentRange } from './moment';
import { Params } from '@angular/router';

export interface IDateRange {
  start: IMoment;
  end: IMoment;
  rawRange?: ISelectedRange
}

export interface IDateTimeRange {
  start: number;
  end: number;
}

export interface IParamSort {
  sort_field?: string;
  sort_direction?: string;
}

export interface ISort {
  field: string;
  direction: string;
}

export enum sortDirection {
  ASCENDING = 'asc',
  DESCENDING = 'desc',
}

export enum DEFAULT_DATE_RANGE_KEYS {
  START_DATE= 'start_date',
  END_DATE= 'end_date',
}

export const PAGE_NO_KEY = 'page_no';
export const PER_PAGE_KEY: string = 'per_page';
export const SM_DATE_FORMAT: string = 'YYYY-MM-DD';
export const SM_DATE_TIME_FORMAT: string = 'YYYY-MM-DDTHH:mm:ss';
export const SORT_FIELD_KEY: string = 'sort_field';
export const SORT_DIRECTION_KEY: string = 'sort_direction';
export const DATE_RANGE_KEY: string = 'dateRange';

export function setDateRangeInMap(queryMap: Map<keyof Params, any>, range: IDateRange): void {
  if (range) {
    queryMap.set('start_date', range.start.format(SM_DATE_FORMAT));
    queryMap.set('end_date', range.end.format(SM_DATE_FORMAT));
  } else {
    queryMap.delete('start_date');
    queryMap.delete('end_date');
  }
}

export function setPhxDateRangeInMap(queryMap: Map<keyof Params, any>, range: ISelectedRange): void {
  if (range) {
    queryMap.set('start_date', range.start);
    queryMap.set('end_date', range.end);
  } else {
    queryMap.delete('start_date');
    queryMap.delete('end_date');
  }
}

export function setPhoenixDateRangeInMap(queryMap: Map<keyof Params, any>, range: ISelectedRange): void {
  if (range) {
    queryMap.set('start_time', range.start);
    queryMap.set('end_time', range.end);
    return;
  }
  queryMap.delete('start_time');
  queryMap.delete('end_time');
}

export function setDateTimeRangePickerInMap(queryMap: Map<keyof Params, any>, range: IDateTimeRange): void {
  if (range) {
    queryMap.set('start_time', range.start);
    queryMap.set('end_time', range.end);
  } else {
    queryMap.delete('start_time');
    queryMap.delete('end_time');
  }
}

function defaultMapValueFromRouteParams(params: Params, stateKey: keyof Params): any {
  return params[stateKey];
}

export function mapDateRangeFromRouteParams(
  params: Params, stateKey: keyof Params, startDateKey: string = 'start_date', endDateKey: string = 'end_date',
): IDateRange {
  if (params[startDateKey] && params[endDateKey]) {
    const start: IMoment = moment(params[startDateKey], SM_DATE_FORMAT);
    const end: IMoment = moment(params[endDateKey], SM_DATE_FORMAT);
    return moment.range(start, end);
  }
  return undefined;
}

export function mapSelectedDateRangeFromRouteParams(
  params: Params, stateKey: keyof Params, startDateKey: string = 'start_date', endDateKey: string = 'end_date',
): ISelectedRange {
  if (params[startDateKey] && params[endDateKey]) {
    const start: Date = parseISO(params[startDateKey]);
    const end: Date = parseISO(params[endDateKey]);
    return { start, end };
  }
  return undefined;
}

export function mapDateTimeRangePickerFromRouteParams(
  params: Params, stateKey: keyof Params, startDateKey: string = 'start_time', endDateKey: string = 'end_time',
): IDateTimeRange {
  if (params[startDateKey] && params[endDateKey]) {
    const start: number = params[startDateKey];
    const end: number = params[endDateKey];
    return { start, end };
  }
  return undefined;
}

export function mapPhoenixDateRangeFromRouteParams(
  params: Params, stateKey: keyof Params, startDateKey: string = 'start_date', endDateKey: string = 'end_date',
): ISelectedRange {
  if (params[startDateKey] && params[endDateKey]) {
    const start: Date = moment(params[startDateKey]).toDate();
    const end: Date = moment(params[endDateKey]).toDate();
    return { start, end };
  }
  return null;
}

export function mapDateTimeRangeFromRouteParams(
  params: Params, stateKey: keyof Params, startDateKey: string = 'start_date', endDateKey: string = 'end_date',
): IDateRange {
  if (params[startDateKey] && params[endDateKey]) {
    const start: IMoment = moment(params[startDateKey], SM_DATE_TIME_FORMAT);
    const end: IMoment = moment(params[endDateKey], SM_DATE_TIME_FORMAT);
    return moment.range(start, end);
  }
  return undefined;
}

export function mapArrayFromRouteParams(params: Params, key: keyof Params): string | string[] {
  if (Array.isArray(params[key])) return params[key];
  const value: string = params[key];
  return value ? value.split(',') : value;
}

export function mapBooleanFromRouteParams(params: Params, key: keyof Params): boolean {
  return params[key] === 'true';
}

export function mapNumberFromRouteParams(params: Params, key: keyof Params): number {
  return +params[key];
}

export function isDefaultSortApplied(defaultSort: ISort, currentSort: IParamSort): boolean {
  return (
    defaultSort?.field === currentSort?.sort_field &&
    defaultSort?.direction === currentSort?.sort_direction
  );
}

export function isDefaultDateRangeApplied(
  defaultRange: IMomentRange | ISelectedRange,
  currentRange:  IMomentRange | ISelectedRange | IDateRange
): boolean {
  const defaultMomentRange: IMomentRange = moment.range(defaultRange?.start, defaultRange?.end);
  const currentMomentRange: IMomentRange = moment.range(currentRange?.start, currentRange?.end);

  return (
    currentMomentRange?.start.isSame(defaultMomentRange?.start, 'day') &&
    currentMomentRange?.end.isSame(defaultMomentRange?.end, 'day')
  );
}

export function removeDefaultSortParams(params: Params): void {
  delete params?.sort_direction;
  delete params?.sort_field;
}

export function removeDefaultDateRangeParams(params: Params): void {
  delete params?.start_date;
  delete params?.end_date;
}

export const setValueIfNotDefault =
  curry((keyInMap: keyof Params, defaultVal: string, queryMap: Map<keyof Params, any>, newVal: string): void => {
    if (!newVal || newVal === defaultVal) queryMap.delete(keyInMap);
    else queryMap.set(keyInMap, newVal);
  });

export interface IStateManagerSetOptions {
  resetPage?: boolean;
  stateKey?: keyof Params;
  mapValueFromRouteParams?: any;
  setQueryValueInMap?: (queryMap: Map<keyof Params, any>, value: any, queryKey: keyof Params) => void;
  isDistinct?: boolean;
  defaultValue?: any;
}

export class StateManager {
  private pageNo$: Subject<number>;
  private stateMap: Params;
  private stateParams$: Subject<Params>;
  private stateActions: Array<Subject<Params>>;
  private queryMap: Map<keyof Params, any>;
  private queryParams$: Subject<Map<keyof Params, any>>;
  private routeParamsSubscription: Subscription;

  constructor() {
    this.stateMap = {};
    this.stateParams$ = new Subject();
    this.stateActions = [];
    this.queryMap = new Map();
    this.queryParams$ = new Subject();
  }

  public subscribeRouteParams(routeParams$: Observable<Params>): void {
    this.routeParamsSubscription = routeParams$
      .pipe(observeOn(asapScheduler))
      .subscribe((params: Params): void => {
        this.stateActions.forEach((stateAction: Subject<Params>): void => stateAction.next(params));
        this.emitStateStream();
        this.emitQueryStream();
      });
  }

  public unsubscribeRouteParams(): void {
    this.routeParamsSubscription?.unsubscribe();
  }

  public getStateParamsMap(): Params {
    return this.stateMap;
  }

  public getStateParamsStream(): Subject<Params> {
    return this.stateParams$;
  }

  public getQueryParamsMap(): Map<any, any> {
    return this.queryMap;
  }

  public getQueryParamsStream(): Subject<Map<keyof Params, any>> {
    return this.queryParams$;
  }

  public nextPage(): void {
    const queryMap = this.getQueryParamsMap();
    this.pageNo$.next(queryMap.get(PAGE_NO_KEY) + 1);
    this.emitQueryStream();
  }

  public refresh(): void {
    if (this.pageNo$) this.pageNo$.next(1);
    this.emitQueryStream();
  }

  public set(queryKey: keyof Params, {
    resetPage = true,
    stateKey = queryKey,
    mapValueFromRouteParams = null,
    setQueryValueInMap = null,
    isDistinct = true,
    defaultValue = null,
  }: IStateManagerSetOptions = {}): this {
    let query$: any = new ReplaySubject(1);
    if (isDistinct) query$ = query$.pipe(distinctUntilChanged());

    if (queryKey === PAGE_NO_KEY) {
      this.pageNo$ = query$;
      this.pageNo$.next(defaultValue || 1);
    }

    if (!mapValueFromRouteParams) mapValueFromRouteParams = defaultMapValueFromRouteParams;

    const state$: Subject<Params> = new Subject();
    this.stateActions.push(state$);

    state$
      .pipe(
        distinctUntilChanged(),
        map((params: Params): any => mapValueFromRouteParams(params, stateKey)),
      )
      .subscribe((value: any): void => {
        const stateMap: Params = this.getStateParamsMap();
        value = !value && defaultValue ? defaultValue : value;
        stateMap[stateKey] = value;
        query$.next(value);
      });

    query$.subscribe((value: any): void => {
      if (value === null) return;

      const queryMap: Map<keyof Params, any> = this.getQueryParamsMap();
      if (resetPage) queryMap.set(PAGE_NO_KEY, 1);

      if (setQueryValueInMap) setQueryValueInMap(queryMap, value, queryKey);
      else if (value) queryMap.set(queryKey, value);
      else queryMap.delete(queryKey);
    });

    return this;
  }

  private emitStateStream(): void {
    this.stateParams$.next(this.getStateParamsMap());
  }

  private emitQueryStream(): void {
    this.queryParams$.next(this.getQueryParamsMap());
  }
}
